/**
 * Categorization + filtering helpers for store copy actions, used by the action filter tabs.
 * "Requires attention" surfaces the integration_not_found action (the main failure mode).
 */
import { IStoreCopyAction } from '../../../../services/api/troubleshooting-service';

export type ActionCategory =
    | 'requires_attention'
    | 'all'
    | 'created'
    | 'updated'
    | 'integration'
    | 'stale';

export interface IActionTab {
    id: ActionCategory;
    label: string;
    count: number;
}

export const isRequiresAttention = (action: IStoreCopyAction): boolean =>
    action.action === 'integration_not_found';

const CATEGORY_PREDICATES: Record<ActionCategory, (action: IStoreCopyAction) => boolean> = {
    requires_attention: isRequiresAttention,
    all: () => true,
    created: (a) => a.action === 'created',
    updated: (a) => a.action === 'updated',
    integration: (a) => a.action === 'integration_linked' || a.action === 'integration_not_found',
    stale: (a) => a.action === 'disabled' || a.action === 'soft_deleted',
};

export const filterActions = (actions: IStoreCopyAction[], category: ActionCategory): IStoreCopyAction[] =>
    actions.filter(CATEGORY_PREDICATES[category]);

const CATEGORY_LABELS: Record<ActionCategory, string> = {
    requires_attention: 'Requires attention',
    all: 'All',
    created: 'Created',
    updated: 'Updated',
    integration: 'Integrations',
    stale: 'Stale cleanup',
};

/**
 * Builds the tab list for a set of actions. "Requires attention" and "All" always appear;
 * the remaining category tabs appear only when they contain at least one action.
 */
export const buildActionTabs = (actions: IStoreCopyAction[]): IActionTab[] => {
    const order: ActionCategory[] = ['requires_attention', 'all', 'created', 'updated', 'integration', 'stale'];
    const alwaysShown: ActionCategory[] = ['requires_attention', 'all'];

    return order
        .map((id) => ({ id, label: CATEGORY_LABELS[id], count: filterActions(actions, id).length }))
        .filter((tab) => alwaysShown.includes(tab.id) || tab.count > 0);
};
